import { GridView, Search, UploadFile, ViewList } from "@mui/icons-material";
import {
  Box,
  Button,
  FormLabel,
  Grid,
  Input,
  InputAdornment,
  MenuItem,
  Paper,
  Stack,
  TablePagination,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { ChangeEventHandler, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ErrorMessage } from "../App/Components/Forms/formHelpers";
import { PageContainer } from "../App/Components/PageContainer/PageContainer";
import { PageTitle } from "../App/Components/PageTitle/PageTitle";
import { PaginationState } from "../App/Components/Table/State/PaginationState";
import { AppFeatureLookup } from "../App/Constants/AbacConstants";
import { crmLiteUpgradeMessage } from "../App/Constants/CrmLiteConstants";
import { CampaignSortBy } from "../App/Enums/CampaignTypes";
import { useFeaturePermission } from "../App/State/Hooks/abacHooks";
import { useIsSuperUser } from "../App/State/Hooks/useIsSuperUser";
import { useNotification } from "../App/State/Hooks/notificationHooks";
import { CampaignActionDictionary } from "../App/Typings/Campaigns";
import { parseImportedFormConfig } from "../CreateCampaign/Helpers/CampaignFunctions";
import { DisplayCampaignList } from "./Components/CampaignDisplay/DisplayCampaignList";
import { DisplayCampaignListLoading } from "./Components/CampaignDisplay/DisplayCampaignListLoading";
import DisplayCampaignTable from "./Components/CampaignDisplay/DisplayCampaignTable";
import { RenderDialog } from "./Components/Dialog/Dialog";
import { useManageCampaignsState } from "./State/ManageCampaignsState";

export default function Campaigns() {
  const { displayError } = useNotification();
  const {
    getters: { searchInputError, paginationState, campaignPagination, error, isLoading, dialogProps, view },
    handlers: {
      handleClearDialog,
      handleOpenDeleteDialog,
      handleOpenEmbedScriptDialog,
      handleOpenViewCampaignDialog,
      handleSearchFilter,
      handleChangeSortBy,
      handleChangeSortDirection,
      handleChangeView,
    },
  } = useManageCampaignsState();

  const actions: CampaignActionDictionary = {
    delete: handleOpenDeleteDialog,
    embed: handleOpenEmbedScriptDialog,
    view: handleOpenViewCampaignDialog,
  };

  useEffect(() => {
    if (error) {
      displayError("An error occurred while fetching forms.");
    }
  }, [error, displayError]);

  return (
    <PageContainer>
      <Paper sx={{ mt: 4, p: { xs: 2, lg: 4 } }}>
        <Box>
          <Box sx={{ marginBottom: "20px" }}>
            <PageTitle sx={{ lineHeight: 1 }}>Manage Forms</PageTitle>
          </Box>
          <Grid container sx={{ marginBottom: "20px" }}>
            <Grid container item xs={12} md={9} lg={6} sx={{ paddingRight: { xs: 0, md: 1, lg: 0 } }}>
              <FilterAndSortCampaigns
                handleSearchFilter={handleSearchFilter}
                searchInputError={searchInputError}
                handleChangeSortBy={handleChangeSortBy}
                handleChangeSortDirection={handleChangeSortDirection}
                paginationState={paginationState}
                view={view}
              />
            </Grid>
            <Grid item xs={0} lg={3} xl={4} sx={{ display: { xs: "none", lg: "block" } }}>
              &nbsp;
            </Grid>
            <Grid
              item
              xs={12}
              md={3}
              xl={2}
              sx={{ paddingLeft: { xs: 0, md: 1, lg: 0 }, paddingTop: { xs: 2, md: 0 } }}
            >
              <Stack
                direction="row"
                spacing={1}
                justifyContent="flex-end"
                sx={{ minHeight: 40, flexWrap: "wrap", rowGap: 1 }}
              >
                <CreateCampaignButton />
                <ImportFormConfigButton />
                <Button
                  variant="outlined"
                  color="info"
                  sx={{ minWidth: 0 }}
                  onClick={() => handleChangeView(view === "card" ? "list" : "card")}
                  data-test="form-view-btn"
                  title={view === "card" ? "Table View" : "Grid View"}
                >
                  {view === "card" ? <ViewList /> : <GridView />}
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ minHeight: "500px" }}>
          {view === "card" ? (
            <>
              {isLoading ? (
                <DisplayCampaignListLoading />
              ) : campaignPagination && campaignPagination.items.length > 0 && !error ? (
                <DisplayCampaignList campaigns={campaignPagination.items} actions={actions} />
              ) : (
                <Typography component="h2" sx={{ fontSize: "1em", padding: 1 }}>
                  No forms to display.
                </Typography>
              )}
              <TablePagination
                rowsPerPageOptions={[10, 15, 25]}
                component="div"
                count={campaignPagination?.totalItems ?? -1}
                rowsPerPage={paginationState.getters.pageSize}
                page={paginationState.getters.page}
                onPageChange={paginationState.handlers.handleChangePage}
                onRowsPerPageChange={paginationState.handlers.handleChangePageSize}
                data-test="campaignsPagination"
                labelRowsPerPage="Forms per page"
              />
            </>
          ) : (
            <DisplayCampaignTable
              campaigns={campaignPagination?.items ?? []}
              isLoading={isLoading}
              pagination={{
                rowsPerPage: [10, 15, 25],
                rows: paginationState.getters.pageSize,
                currentPage: paginationState.getters.page,
                handleChangePage: paginationState.handlers.handleChangePage,
                handleChangeRowsPerPage: paginationState.handlers.handleChangePageSize,
                enabled: !isLoading,
                totalCount: campaignPagination?.totalItems ?? -1,
              }}
              actions={actions}
              sortBy={paginationState.getters.sortBy}
              sort={paginationState.getters.sortDirection}
              onSort={paginationState.handlers.handleRequestSort}
            />
          )}
        </Box>
      </Paper>
      <RenderDialog open={dialogProps.open ?? false} handleClearDialog={handleClearDialog} {...dialogProps} />
    </PageContainer>
  );
}

const CreateCampaignButton = () => {
  const accessCreateCampaignButton = useFeaturePermission(AppFeatureLookup.CampaignsCreateCampaign);
  return (
    <Tooltip title={!accessCreateCampaignButton ? crmLiteUpgradeMessage : ""} arrow>
      <Box sx={{ height: "100%" }}>
        <Button
          fullWidth
          component={Link}
          to={accessCreateCampaignButton ? "/leadcapture/forms/create" : ""}
          data-test="create-campaign-link"
          color="primary"
          variant="contained"
          sx={{ height: "100%" }}
          disabled={!accessCreateCampaignButton}
        >
          Create Form
        </Button>
      </Box>
    </Tooltip>
  );
};

const ImportFormConfigButton = () => {
  const isSuperUser = useIsSuperUser();
  const { displayError } = useNotification();
  const navigate = useNavigate();

  const handleUploadFile: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) {
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(await file.text());
    } catch {
      displayError("That file isn't a valid form configuration.");
      return;
    }

    try {
      const importedFormState = parseImportedFormConfig(parsed);
      navigate("/leadcapture/forms/create", { state: { importedFormState } });
    } catch (err) {
      displayError(err instanceof Error ? err.message : "That file isn't a valid form configuration.");
    }
  };

  // Import is a super-user-only feature, hidden entirely (not just disabled) for everyone else
  if (!isSuperUser) {
    return null;
  }

  return (
    <Box sx={{ height: "100%" }}>
      <FormLabel htmlFor="import-form-config-input" sx={{ display: "block", height: "100%" }}>
        <Input
          id="import-form-config-input"
          type="file"
          inputProps={{ accept: "application/json,.json", "data-test": "import-form-config-input" }}
          style={{ display: "none" }}
          onChange={handleUploadFile}
        />
        <Button
          fullWidth
          component="span"
          data-test="import-form-config-button"
          color="info"
          variant="outlined"
          sx={{ height: "100%" }}
          startIcon={<UploadFile />}
        >
          Import Form
        </Button>
      </FormLabel>
    </Box>
  );
};

const FilterAndSortCampaigns = ({
  handleSearchFilter,
  searchInputError,
  handleChangeSortBy,
  handleChangeSortDirection,
  paginationState,
  view,
}: {
  handleSearchFilter: (e: React.ChangeEvent<HTMLInputElement>) => void;
  searchInputError: string;
  handleChangeSortBy: (sortBy: CampaignSortBy) => void;
  handleChangeSortDirection: (sortDirection: "asc" | "desc") => void;
  paginationState: PaginationState;
  view: "card" | "list";
}) => {
  return (
    <>
      <Grid item xs={12} md={6} sx={{ paddingBottom: { xs: 2, md: 0 } }}>
        <TextField
          label="Filter Forms"
          defaultValue={paginationState.getters.search}
          onChange={handleSearchFilter}
          type="search"
          fullWidth
          error={searchInputError.length > 0}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
          }}
          data-test="campaigns-searchbar"
        />
        {searchInputError && <ErrorMessage message={searchInputError} />}
      </Grid>
      {view === "card" ? (
        <>
          <Grid item xs={12} md={3} sx={{ paddingLeft: { xs: 0, md: 1 }, paddingBottom: { xs: 2, md: 0 } }}>
            <TextField
              label="Sort By"
              onChange={(e) => handleChangeSortBy(e.target.value as CampaignSortBy)}
              fullWidth
              data-test="campaigns-sortby"
              select
              value={
                paginationState.getters.sortBy[0].toUpperCase() + paginationState.getters.sortBy.slice(1)
              }
            >
              <MenuItem value={CampaignSortBy.Name}>Name</MenuItem>
              <MenuItem value={CampaignSortBy.Count}>Capture Count</MenuItem>
              <MenuItem value={CampaignSortBy.LastSubmissionDate}>Last Submission Date</MenuItem>
              <MenuItem value={CampaignSortBy.LastUpdated}>Last Updated Date</MenuItem>
              <MenuItem value={CampaignSortBy.CreatedDate}>Created Date</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} md={3} sx={{ paddingLeft: { xs: 0, md: 1 } }}>
            <TextField
              label="Sort Direction"
              onChange={(e) => handleChangeSortDirection(e.target.value as "asc" | "desc")}
              fullWidth
              data-test="campaigns-sort-direction"
              select
              value={paginationState.getters.sortDirection}
            >
              <MenuItem value="asc">Ascending</MenuItem>
              <MenuItem value="desc">Descending</MenuItem>
            </TextField>
          </Grid>
        </>
      ) : (
        <></>
      )}
    </>
  );
};
